import { useEffect, useRef } from "react";
import { last, isEmpty } from "lodash-es";
import { SessionItem, SessionItemKind } from "@/mds/components/sessions/session-item";

export const useEscapeKeyHandler = (isArtifactOpen: boolean, closeArtifact: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeArtifact();
      }
    };

    if (isArtifactOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isArtifactOpen, closeArtifact]);
};

/**
 * Determines if a reasoning group should be auto-collapsed based on session state changes
 */
export const getReasoningGroupToAutoCollapse = ({
  prevItems,
  currentItems,
  reasoningGroups,
}: {
  prevItems: SessionItem[];
  currentItems: SessionItem[];
  reasoningGroups: Record<string, { userToggled?: boolean; isCollapsed?: boolean }>;
}): string | null => {
  if (isEmpty(prevItems)) return null;

  const prevLastItem = last(prevItems);
  if (prevLastItem?.kind !== SessionItemKind.ReasoningGroup) return null;

  const prevGroupId = prevLastItem.id;
  const currentLastItem = last(currentItems);
  const isStillLast = currentLastItem?.id === prevGroupId;
  if (isStillLast) return null;

  const groupState = reasoningGroups[prevGroupId];
  if (groupState?.userToggled || groupState?.isCollapsed) return null;

  return prevGroupId;
};

/**
 * Hook that auto-collapses reasoning groups when they are no longer the last item
 */
export const useAutoCollapseReasoningGroups = ({
  sessionItems,
  reasoningGroups,
  autoCollapseReasoningGroup,
}: {
  sessionItems: SessionItem[];
  reasoningGroups: Record<string, { userToggled?: boolean; isCollapsed?: boolean }>;
  autoCollapseReasoningGroup: (groupId: string) => void;
}) => {
  const prevSessionItemsRef = useRef<SessionItem[]>([]);

  useEffect(() => {
    const prevItems = prevSessionItemsRef.current;

    const groupToCollapse = getReasoningGroupToAutoCollapse({
      prevItems,
      currentItems: sessionItems,
      reasoningGroups,
    });

    if (groupToCollapse) {
      autoCollapseReasoningGroup(groupToCollapse);
    }

    prevSessionItemsRef.current = sessionItems;
  }, [sessionItems, reasoningGroups, autoCollapseReasoningGroup]);
};
